import DOMPurify from 'dompurify';

export interface ParsedPOI {
  name: string;
  latitude: number;
  longitude: number;
  category?: string;
  city?: string;
  country?: string;
  description?: string;
  selected: boolean;
  urls?: string[];
  images?: string[];
  htmlDescription?: string;
}

export interface ParsedCard {
  category: string;
  text: string;
  language_code: string;
  selected: boolean;
  poiIndex: number; // Index to link card to POI
  urls?: string[];
  images?: string[];
  htmlText?: string;
}

export interface ParsedKMLData {
  pois: ParsedPOI[];
  cards: ParsedCard[];
}

/**
 * Extract Map ID from Google MyMaps URL
 * Example URL: https://www.google.com/maps/d/u/0/viewer?mid=1eFoBdbrHPM7CjBpUYS7xkrfPzr_xYgc&femb=1&ll=44.44023845844435%2C26.0957697719103&z=12
 */
export const extractMapId = (url: string): string | null => {
  try {
    const urlObj = new URL(url);
    const mid = urlObj.searchParams.get('mid');
    return mid;
  } catch (error) {
    console.error('Error parsing URL:', error);
    return null;
  }
};

/**
 * Fetch KML data from Google MyMaps
 */
export const fetchKMLFromGoogleMaps = async (mapId: string): Promise<string> => {
  const kmlUrl = `https://www.google.com/maps/d/kml?mid=${mapId}&forcekml=1`;
  
  try {
    const response = await fetch(kmlUrl, {
      method: 'GET',
      headers: {
        'Accept': 'application/vnd.google-earth.kml+xml',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch KML: ${response.status} ${response.statusText}`);
    }

    const kmlText = await response.text();
    return kmlText;
  } catch (error) {
    console.error('Error fetching KML:', error);
    throw new Error('Failed to fetch KML data. Make sure the map is set to "Public" or "Anyone with the link can view".');
  }
};

/**
 * Parse coordinates from KML coordinate string
 * Format: "longitude,latitude,altitude" or just "longitude,latitude"
 */
const parseCoordinates = (coordString: string): { latitude: number; longitude: number } | null => {
  try {
    const parts = coordString.trim().split(',');
    if (parts.length >= 2) {
      const longitude = parseFloat(parts[0]);
      const latitude = parseFloat(parts[1]);
      
      if (!isNaN(latitude) && !isNaN(longitude)) {
        return { latitude, longitude };
      }
    }
    return null;
  } catch (error) {
    console.error('Error parsing coordinates:', error);
    return null;
  }
};

/**
 * Extract text content from XML element
 */
const getElementText = (element: Element | null): string => {
  if (!element) return '';
  return element.textContent?.trim() || '';
};

/**
 * Extract URLs from text content
 * Improved pattern to avoid capturing trailing punctuation and brackets
 * Validates URLs to ensure they use http/https protocols
 */
const extractUrls = (text: string): string[] => {
  if (!text) return [];
  // More precise pattern that stops before trailing punctuation and brackets
  const urlRegex = /(https?:\/\/[^\s<>"[\]{}]+?)(?=[.,;:!?)\]}\s]|$)/gi;
  const matches = text.match(urlRegex);
  
  // Validate URLs to ensure they're safe
  const safeUrls = matches?.filter(url => {
    try {
      const urlObj = new URL(url);
      // Only allow http/https protocols
      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
    } catch {
      return false;
    }
  });
  
  return safeUrls || [];
};

/**
 * Extract image URLs from text content (common image extensions)
 * Case-insensitive matching with improved pattern
 * Note: SVG files are intentionally excluded to prevent potential script execution
 */
const extractImages = (text: string): string[] => {
  if (!text) return [];
  // Case-insensitive pattern for image URLs (excludes SVG for security)
  const imageRegex = /(https?:\/\/[^\s<>"]+?\.(?:jpe?g|png|gif|webp)(?:\?[^\s<>"]*)?)/gi;
  const matches = text.match(imageRegex);
  
  // Filter out potentially malicious URLs (basic validation)
  const safeImages = matches?.filter(url => {
    try {
      const urlObj = new URL(url);
      // Only allow http/https protocols
      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
    } catch {
      return false;
    }
  });
  
  return safeImages || [];
};

/**
 * Extract and sanitize HTML content from CDATA sections
 * Uses DOMPurify to prevent XSS attacks
 */
const extractHTMLContent = (text: string): string | undefined => {
  if (!text) return undefined;
  // Check if text contains HTML tags or CDATA
  if (text.includes('<') && text.includes('>')) {
    // Sanitize HTML to prevent XSS
    const sanitized = DOMPurify.sanitize(text, {
      ALLOWED_TAGS: ['p', 'br', 'b', 'i', 'u', 'strong', 'em', 'a', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'code', 'pre'],
      ALLOWED_ATTR: ['href', 'target', 'rel'],
      ALLOW_DATA_ATTR: false,
    });
    return sanitized;
  }
  return undefined;
};

/**
 * Parse KML XML into POI and Card data structures using native DOMParser
 */
export const parseKML = async (kmlXML: string): Promise<ParsedKMLData> => {
  const pois: ParsedPOI[] = [];
  const cards: ParsedCard[] = [];

  try {
    // Parse XML using browser's DOMParser
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(kmlXML, 'text/xml');

    // Check for parsing errors
    const parserError = xmlDoc.querySelector('parsererror');
    if (parserError) {
      throw new Error('Failed to parse KML XML');
    }

    // Get all Placemark elements
    const placemarks = xmlDoc.querySelectorAll('Placemark');

    placemarks.forEach((placemark, index) => {
      // Extract name
      const nameElement = placemark.querySelector('name');
      const name = getElementText(nameElement) || `POI ${index + 1}`;
      
      // Extract description
      const descriptionElement = placemark.querySelector('description');
      const description = getElementText(descriptionElement);
      
      // Extract coordinates from Point, LineString, or Polygon
      let coords = null;
      
      // Try Point first
      const pointCoords = placemark.querySelector('Point > coordinates');
      if (pointCoords) {
        const coordString = getElementText(pointCoords);
        coords = parseCoordinates(coordString);
      }
      
      // Try LineString if no Point found
      if (!coords) {
        const lineStringCoords = placemark.querySelector('LineString > coordinates');
        if (lineStringCoords) {
          const coordString = getElementText(lineStringCoords);
          // For lines, take the first point
          const firstPoint = coordString.split(/\s+/)[0];
          coords = parseCoordinates(firstPoint);
        }
      }
      
      // Try Polygon if no Point or LineString found
      if (!coords) {
        const polygonCoords = placemark.querySelector('Polygon outerBoundaryIs LinearRing coordinates');
        if (polygonCoords) {
          const coordString = getElementText(polygonCoords);
          // For polygons, take the first point
          const firstPoint = coordString.split(/\s+/)[0];
          coords = parseCoordinates(firstPoint);
        }
      }

      if (coords) {
        // Extract URLs and images from description
        const urls = extractUrls(description);
        const images = extractImages(description);
        const htmlDescription = extractHTMLContent(description);

        // Create POI
        const poi: ParsedPOI = {
          name,
          latitude: coords.latitude,
          longitude: coords.longitude,
          description,
          selected: true, // Default to selected
          urls: urls.length > 0 ? urls : undefined,
          images: images.length > 0 ? images : undefined,
          htmlDescription,
        };

        pois.push(poi);

        // Create Card from description if exists
        if (description && description.trim().length > 0) {
          const card: ParsedCard = {
            category: 'description',
            text: description,
            language_code: 'en', // Default to English - TODO: Make configurable or detect from content
            selected: true,
            poiIndex: pois.length - 1, // Link to the POI we just added
            urls: urls.length > 0 ? urls : undefined,
            images: images.length > 0 ? images : undefined,
            htmlText: htmlDescription,
          };
          cards.push(card);
        }
      }
    });

    return { pois, cards };
  } catch (error) {
    console.error('Error processing KML data:', error);
    throw new Error('Failed to process KML data');
  }
};

/**
 * Main function to import Google MyMaps data
 */
export const importGoogleMyMaps = async (url: string): Promise<ParsedKMLData> => {
  // Extract Map ID
  const mapId = extractMapId(url);
  if (!mapId) {
    throw new Error('Invalid Google MyMaps URL. Could not extract map ID.');
  }

  // Fetch KML data
  const kmlXML = await fetchKMLFromGoogleMaps(mapId);

  // Parse KML
  const parsedData = await parseKML(kmlXML);

  return parsedData;
};
